import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
// MOOSE storefront search aliases.
// Japanese and common phonetic inputs are resolved to the storefront/vendor term
// that Shopify search is more likely to match.
const SEARCH_ALIASES = {
  'アクリマ': 'ACLIMA',
  'アロックサック': 'aLOKSAK',
  'アルピナ': 'Alpina',
  'アルトラ': 'ALTRA',
  'アンサー4': 'ANSWER4',
  'アンサー': 'ANSWER4',
  'アライテント': 'ARAI TENT',
  'アスピラボ': 'ASPILABO',
  'アストラル': 'ASTRAL',
  'アトラス': 'ATLAS',
  'アクシーズクイン': 'AXESQUIN',
  'アクシーズクインエレメンツ': 'AXESQUIN ELEMENTS',
  'ベアボーンズ': 'BAREBONES',
  'ビーシーエー': 'bca',
  'ベルモント': 'BELMONT',
  'ビッグアグネス': 'BIG AGNES',
  'ビッグスカイ': 'BIG SKY INTERNATIONAL',
  'ブラックダイヤモンド': 'Black Diamond',
  'ブラストラック': 'BLASTRACK',
  'ブレンダーボトル': 'BLENDER BOTTLE',
  'ブルーアイス': 'BLUE ICE',
  'ブックマン': 'BOOKMAN',
  'バックナイフ': 'BUCK KNIVES',
  'ブッシュクラフト': 'Bush craft',
  'キャンプ': 'CAMP',
  'キャリーザサン': 'CARRY THE SUN',
  'チャオラス': 'CHAORAS',
  'クノック': 'CNOC OUTDOORS',
  'クノックアウトドア': 'CNOC OUTDOORS',
  'コクーン': 'COCOON',
  'コフラン': 'COGHLANS',
  'コグラン': 'COGHLANS',
  'コグネイティブ': 'COGNATIVE MTB',
  'コニファーコーン': 'conifer cone',
  'コントゥア': 'contour',
  'クレイジークリーク': 'CRAZY CREEK',
  'クリスピー': 'CRISPI',
  'クラックス': 'CRUX',
  'ディービー': 'DB',
  'ドライマックス': 'DRYMAX',
  'ディナフィット': 'DYNAFIT',
  'エーデルリッド': 'EDELRID',
  'エバニュー': 'EVERNEW',
  'エクソタック': 'EXOTAC',
  'エクスペド': 'EXPED',
  'エキスパートオブジャパン': 'EXPERT OF JAPAN',
  'エクストリミティーズ': 'extremities',
  'ファブパッチ': 'FABPATCH',
  'ファイントラック': 'finetrack',
  'ファイヤーボックス': 'Firebox',
  'ファイアーボックス': 'Firebox',
  'ファイヤードラゴン': 'FIREDRAGON',
  'フィッシャー': 'FISCHER',
  'フィッツ': 'FITS',
  'フレックステイル': 'FLEXTAIL',
  'フロート': 'FLOAT',
  'フラックス': 'flux',
  'フォークラム': 'folkrm',
  'フォーウインズ': 'FORE WINDS',
  'フォレスタブル': 'FORESTABLE',
  'フォジルズ': 'Fozzils',
  'フリッチ': 'FRITSCHI',
  'ジーストーブ': 'G-Stove',
  'ジースリー': 'G3',
  'ゲイトハプンズ': 'Gait Happens',
  'ガリウム': 'GALLIUM',
  'ギアエイド': 'GEAR AID',
  'ゴーライト': 'GO LITE',
  'ゴーグルソック': 'gogglesoc',
  'グランジャーズ': "Granger's",
  'グラナイトギア': 'GRANITE GEAR',
  'グレートコッシーマウンテン': 'GREAT COSSY MOUNTAIN',
  'グリベル': 'GRIVEL',
  'ガーニーグー': 'GURNEY GOO',
  'ホグロフス': 'Haglofs',
  'ハグロフス': 'Haglofs',
  'ハンズオングリップ': 'handson grip',
  'ヘリノックス': 'Helinox',
  'ヘリテイジ': 'HERITAGE',
  'ヘストラ': 'HESTRA',
  'ハイマウント': 'HIGHMOUNT',
  'ハイク': 'HIKE',
  'ハイカートラッシュ': 'HIKER TRASH',
  'ヒルサウンド': 'HILLSOUND',
  'ホロ': 'holo',
  'フーディニ': 'HOUDINI',
  'ハルタフォース': 'Hultafors',
  'ハイドラパック': 'Hydrapak',
  'アイベックス': 'IBEX',
  'アイスミュール': 'ICEMULE',
  'イフユーハブ': 'IF YOU HAVE',
  'イイヅカ': 'IIZUKA',
  'インジンジ': 'Injinji',
  'イノヴェイト': 'inov-8',
  'イノベイト': 'inov-8',
  'イスカ': 'ISUKA',
  'ジェットボイル': 'JETBOIL',
  'ケーツー': 'K2',
  'カモック': 'KAMMOK',
  'カング': 'KANG',
  'キバアウトドア': 'KIVA OUTDOORS',
  'クレッタルムーセン': 'KLATTERMUSEN',
  'クレッタルムセン': 'KLATTERMUSEN',
  'コーラ': 'KOHLA',
  'クピルカ': 'KUPILKA',
  'ラスポルティバ': 'LA SPORTIVA',
  'スポルティバ': 'LA SPORTIVA',
  'レキ': 'LEKI',
  'ライフベンチャー': 'LiFEVENTURE',
  'リンデン': 'LINDEN',
  'ルナサンダル': 'LUNA SANDALS',
  'マズシャス': 'MADSHUS',
  'マジックマウンテン': 'MAGIC MOUNTAIN',
  'マムート': 'MAMMUT',
  'マタドール': 'Matador',
  'メトリウス': 'METOLIUS',
  'ミアー': 'MiiR',
  'マイルストーン': 'milestone',
  'ミヤゲン': 'MIYAGEN',
  'モノラル': 'MONORAL',
  'マウントトゥコースト': 'Mount to Coast',
  'マウンテンキング': 'Mountain King',
  'マウンテンローギア': 'Mountain LOWGEAR',
  'エムエスアール': 'MSR',
  'ミュニーク': 'MUNIEQ',
  'ニーモ': 'NEMO',
  'ネバーウェイステッド': 'NEVER WASTED',
  'ニューハレ': 'New-HALE',
  'ニクワックス': 'NIKWAX',
  'ノーボックス': 'NOBOX',
  'ノローナ': 'NORRONA',
  'ノルテック': 'NORTEC',
  'ノータス': 'Notace',
  'ノヴァスコシアフィッシャーマン': 'NOVA SCOTIA FISHERMAN',
  'オカラ': 'Okara',
  'オルファ': 'OLFA WORKS',
  'オルファワークス': 'OLFA WORKS',
  'オンブラズ': 'OMBRAZ',
  'オーエムエム': 'OMM',
  'オピネル': 'OPINEL',
  'オルトボックス': 'ORTOVOX',
  'オスプレー': 'OSPREY',
  'アウトドアリサーチ': 'OUTDOOR RESEARCH',
  'ムース': 'OUTDOOR SPECIALITY MOOSE',
  'アウトドアユーブイ': 'OUTDOOR UV',
  'アウターユー': 'OuterU',
  'パランテ': "PA'LANTE",
  'パーゴワークス': 'PaaGo WORKS',
  'パックタオル': 'PackTowl',
  'パックセーフ': 'pacsafe',
  'パタゴニア': 'Patagonia',
  'ピークデザイン': 'Peak Design',
  'ピークパフォーマンス': 'PeakPerformance',
  'パーマンドバトン': 'perm and baton',
  'ペツル': 'PETZL',
  'ピープス': 'PIEPS',
  'プラティパス': 'Platypus',
  'プラスワンワークス': 'PLUS ONE WORKS',
  'ポック': 'POC',
  'ポモカ': 'POMOCA',
  'プリムス': 'PRIMUS',
  'プロテック': 'PRO-TEC ATHLETICS',
  'プロテックアスレチックス': 'PRO-TEC ATHLETICS',
  'ラブ': 'Rab',
  'ロウロウ': 'RawLow Mountain Works',
  'ロウロウマウンテンワークス': 'RawLow Mountain Works',
  'アールエムユー': 'RMU',
  'ロッテフェラ': 'Rottefella',
  'サロモン': 'SALOMON',
  'サマヤ': 'SAMAYA',
  'ソーヤー': 'SAWYER PRODUCTS',
  'ソーヤープロダクツ': 'SAWYER PRODUCTS',
  'スカルパ': 'SCARPA',
  'シートゥサミット': 'SEA TO SUMMIT',
  'シナノ': 'SHINANO',
  'ショーワ': 'SHOWA',
  'シルバ': 'SILVA',
  'シックスムーンデザインズ': 'SixMoon Designs',
  'スマートウール': 'Smartwool',
  'スミス': 'SMITH',
  'スノーライン': 'snowline',
  'ソル': 'SOL',
  'ソロストーブ': 'SOLO STOVE',
  'ソト': 'SOTO',
  'エスピーエルシー': 'SPLC',
  'スタティック': 'STATIC',
  'ストーム': 'STORM',
  'ストライド': 'STRIDE',
  'スワニー': 'SWANY',
  'スイートプロテクション': 'Sweet Protection',
  'テラノヴァ': 'TERRA NOVA',
  'ティートンブロス': 'TETON BROS.',
  'ザエムイクイップメント': 'The M Equipment',
  'エムイクイップメント': 'The M Equipment',
  'ノースフェイス': 'THE NORTH FACE',
  'ザノースフェイス': 'THE NORTH FACE',
  'ザパーフェクトアンダーウェア': 'The Perfect Underwear',
  'サーマレスト': 'THERMAREST',
  'サーモス': 'THERMOS',
  'タイニー': 'TiNY',
  'ティーエムアール': 'TMR industries',
  'トークス': 'TOAKS',
  'トコ': 'TOKO',
  'トポデザイン': 'Topo Designs',
  'トレイルバム': 'TRAIL BUM',
  'トランギア': 'trangia',
  'トライトンシル': 'Tritensil',
  'タブス': 'tubbs',
  'ユーコ': 'UCO',
  'アルティメイトディレクション': 'ULTIMATE DIRECTION',
  'アルティメットディレクション': 'ULTIMATE DIRECTION',
  'ウルトラスパイア': 'UltrAspire',
  'アンウェイステッド': 'UNWASTED',
  'バーゴ': 'VARGO',
  'ベロスピカ': 'Velo Spica',
  'ヴィヴァフデ': 'VIVAHDE',
  'ビバフデ': 'VIVAHDE',
  'ビボベアフット': 'VIVO BAREFOOT',
  'ヴィヴォベアフット': 'VIVO BAREFOOT',
  'ボレー': 'VOILE',
  'ウェルビー': 'Wellbe',
  'ワイルド': 'WILDO',
  'ワークラウン': 'WORKROWN',
  'ゼロシューズ': 'XERO SHOES',
  'ヤケーヌ': 'YAKeNU',
  'ゼンビビィ': 'ZENBIVY',
  'ゼンビビー': 'ZENBIVY',
  'ゼログラム': 'ZEROGRAM',
  'トゥエンティツーデザインズ': '22 Designs',
  'ツーデザインズ': '22 Designs',
  'セブンメッシュ': '7MESH',
  'ヒルサガリノジョニー': 'ヒル下がりのジョニー',
  'ジョニー': 'ヒル下がりのジョニー',
  'シノギ': '凌',
  'リョウ': '凌',

  // Category aliases from the public navigation.
  'メンズ': 'MEN',
  '男性用': 'MEN',
  '男': 'MEN',
  'ウィメンズ': 'WOMEN',
  'レディース': 'WOMEN',
  '女性用': 'WOMEN',
  '女': 'WOMEN',
  'バッグ': 'BAG',
  'バック': 'BAG',
  'ザック': 'backpack',
  'バックパック': 'backpack',
  'リュック': 'backpack',
  'トート': 'tote',
  'トートバッグ': 'tote',
  'ショルダー': 'shoulder bag',
  'ショルダーバッグ': 'shoulder bag',
  'ウエストバッグ': 'waist bag',
  'ポーチ': 'pouch',
  'スタッフサック': 'stuff sack',
  'ドライバッグ': 'dry bag',
  'レインカバー': 'rain cover',
  'アウトドアギア': 'OUTDOORGEAR',
  'ギア': 'OUTDOORGEAR',
  '登山用品': 'OUTDOORGEAR',
  'ジャケット': 'jacket',
  'ダウン': 'down',
  '中綿': 'insulation',
  'シェル': 'shell',
  'シェルジャケット': 'shell jacket',
  'ウィンドシェル': 'wind shell',
  'ミッドレイヤー': 'mid layer',
  'トップス': 'tops',
  'シャツ': 'shirt',
  'ロングスリーブ': 'long sleeve',
  'ロンT': 'long sleeve',
  'ショートスリーブ': 'short sleeve',
  'Tシャツ': 't-shirt',
  'ティーシャツ': 't-shirt',
  'ベースレイヤー': 'base layer',
  'パンツ': 'pants',
  'ロングパンツ': 'long pants',
  'ショートパンツ': 'short pants',
  'ショーツ': 'shorts',
  'シューズ': 'SHOES',
  '靴': 'SHOES',
  'トレッキングシューズ': 'trekking shoes',
  'アプローチシューズ': 'approach shoes',
  'ランニング': 'running',
  'トレイルランニング': 'trail running',
  'トレラン': 'trail running',
  'サンダル': 'sandals',
  'シューズアクセサリ': 'shoes accessories',
  '帽子': 'cap',
  'ハット': 'hat',
  'キャップ': 'cap',
  'ニット帽': 'beanie',
  'ビーニー': 'beanie',
  'アクセサリ': 'accessories',
  'アクセサリー': 'accessories',
  'アイウェア': 'eyewear',
  'サングラス': 'sunglasses',
  'グローブ': 'gloves',
  '手袋': 'gloves',
  'ソックス': 'socks',
  '靴下': 'socks',
  'テント': 'tent',
  'タープ': 'tarp',
  'ハンモック': 'hammock',
  'シュラフ': 'sleeping bag',
  '寝袋': 'sleeping bag',
  'スリーピングバッグ': 'sleeping bag',
  'マット': 'mat',
  'ビビィ': 'bivy',
  'ビビィサック': 'bivy',
  'ライト': 'light',
  'ランタン': 'lantern',
  '時計': 'watch',
  'GPS': 'gps',
  '電子機器': 'electronics',
  'トレッキングポール': 'trekking pole',
  'トレランポール': 'trail running pole',
  'ポール': 'pole',
  'ヘルメット': 'helmet',
  'クライミング': 'climbing',
  'クックウェア': 'cookware',
  'テーブル': 'table',
  'チェア': 'chair',
  'クッカー': 'cooker',
  'カップ': 'cup',
  'カトラリー': 'cutlery',
  'ボトル': 'bottle',
  'ハイドレーション': 'hydration',
  'ストーブ': 'stove',
  '燃料': 'fuel',
  'リペア': 'repair',
  'メンテナンス': 'maintenance',
  'タオル': 'towel',
  'セール': 'SALE',
  '即日': '即日',
  '在庫あり': 'available',
  '在庫切れ': 'sold out',
  '売り切れ': 'sold out',
  '新着': 'new',
  '再入荷': 'restock',
  'レストック': 'restock',

  // Product/model aliases commonly seen in MOOSE product names.
  'ローンピーク': 'LONE PEAK',
  'ローンピークワイド': 'LONE PEAK WIDE',
  'ローンピーク防水': 'LONE PEAK WP',
  'オリンパス': 'OLYMPUS',
  'ティンプ': 'TIMP',
  'ティンプボア': 'TIMP BOA',
  'スペリオール': 'SUPERIOR',
  'エスカランテ': 'ESCALANTE',
  'エスカランテレーサー': 'ESCALANTE RACER',
  'エクスペリエンスワイルド': 'EXPERIENCE WILD',
  'バニッシュ': 'VANISH',
  'ヴァニッシュ': 'VANISH',
  'バニッシュパルス': 'VANISH PULSE',
  'ヴァニッシュパルス': 'VANISH PULSE',
  'キングMT': 'King MT',
  'キングエムティー': 'King MT',
  'モンブラン': 'MONT BLANC',
  'トレイルゲイター': 'TRAIL GAITER',
  'ゲイター': 'GAITER',
  'ファントム': 'Phantom',
  'ベイル': 'Veil',
  'ミューオン': 'Muon',
  'ムオン': 'Muon',
  'アドリフト': 'ADRIFT',
  'アポストル': 'Apostle',
  'ヤク': 'YAK',
  'アグラ': 'AGURA',
  'ホーボー': 'Hobo',
  'デイドリーマー': 'DAY DREAMER',
  'ゼンモンク': 'ZEN MONK',
  'トレイルマジック': 'TRAIL MAGIC',
  'ウルトラ100': 'Ultra 100',
  'DNAベスト': 'DNA Vest',
  'トレイルソックス': 'Trail Socks',
  'トレイルキャップ': 'Trail Cap',
  'トラッカー': 'TRACKER',
  'センサス': 'SENSUS',
  'ベナード': 'Venado',
  'ヴェナード': 'Venado',
  'ジャッカル': 'JACKAL',
  'ダックビル': 'Duckbill'
};

function normalizeSearchAliasKey(value) {
  return value
    .trim()
    .normalize('NFKC')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[ぁ-ゖ]/g, (char) => String.fromCharCode(char.charCodeAt(0) + 0x60));
}

function resolveSearchTerm(searchTerm) {
  const key = normalizeSearchAliasKey(searchTerm);

  for (const [alias, resolvedTerm] of Object.entries(SEARCH_ALIASES)) {
    if (normalizeSearchAliasKey(alias) === key) {
      return resolvedTerm;
    }
  }

  return searchTerm;
}

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }
onSearchSubmit = () => {
  const { searchInput } = this.refs;
  searchInput.value = resolveSearchTerm(searchInput.value);
};
  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', resolveSearchTerm(this.refs.searchInput.value));
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
  if (!this.dataset.sectionId) return;

  const lang = document.documentElement.lang?.toLowerCase() || '';
  const useCustomSuggest = lang.startsWith('ja');

  const sectionId = useCustomSuggest ? 'custom-search-suggest' : this.dataset.sectionId;
  const url = new URL(
    useCustomSuggest ? Theme.routes.search_url : Theme.routes.predictive_search_url,
    location.origin
  );

  url.searchParams.set('q', resolveSearchTerm(searchTerm));

  if (useCustomSuggest) {
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
  } else {
    url.searchParams.set('resources[limit_scope]', 'each');
  }

  const { predictiveSearchResults } = this.refs;
  const abortController = this.#createAbortController();

  sectionRenderer
    .getSectionHTML(sectionId, false, url)
    .then((resultsMarkup) => {
      if (!resultsMarkup) return;
      if (abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();
    })
    .catch((error) => {
      if (abortController.signal.aborted) return;
      console.error(error);
    });
}

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
